import { Link } from "@tanstack/react-router";
import { ArrowRight, CircleDashed } from "lucide-react";
import { cn } from "@atrix-analytics/ui";
import { sectionByKey, type SectionKey } from "../nav";
import { useWorkspace } from "../lib/workspace";

/** The designed "not yet" state for sections that ship in a later release — never a broken page. */
export function ComingSoonPage({ section }: { section: SectionKey }) {
  const s = sectionByKey(section);
  const { project } = useWorkspace();
  const Icon = s.icon;
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 py-6 sm:py-12">
      <div
        className={cn(
          "grid size-12 place-items-center rounded-lg border",
          s.ai ? "ai-ring border-transparent bg-bg-raised text-ai" : "border-border bg-bg-raised text-fg-muted",
        )}
      >
        <Icon aria-hidden className="size-5" />
      </div>
      <div>
        <p className="flex items-center gap-1.5 text-xs font-medium text-fg-muted">
          <CircleDashed aria-hidden className="size-3.5" /> Coming in a later release · {s.release}
        </p>
        <h1 className={cn("mt-2 font-display text-2xl font-semibold tracking-tight", s.ai && "grad-text-ai")}>{s.label}</h1>
        <p className="mt-2 text-[15px] text-fg-muted">{s.summary}</p>
      </div>
      <div className="rounded-lg border border-border bg-bg-raised p-5">
        <h2 className="text-sm font-semibold">What it will do</h2>
        <ul className="mt-3 space-y-2.5">
          {s.planned.map((p) => (
            <li key={p} className="flex gap-2.5 text-sm text-fg-muted">
              <span aria-hidden className={cn("mt-2 size-1.5 shrink-0 rounded-full", s.ai ? "bg-ai" : "bg-fg-faint")} />
              {p}
            </li>
          ))}
        </ul>
      </div>
      <p className="text-sm text-fg-muted">
        In the meantime, your events are ready to analyse.{" "}
        <Link
          to="/p/$projectId/insights/trends"
          params={{ projectId: project.id }}
          className="inline-flex items-center gap-1 font-medium text-accent underline-offset-4 hover:underline"
        >
          Explore them in Trends <ArrowRight aria-hidden className="size-3.5" />
        </Link>
      </p>
    </div>
  );
}
